/**
 * Find (brief §5 item 8): a CIS number opens its record; any other text is
 * searched in each searchable object's list, through the list's own `q`
 * (One feedback FB-053/054), with each list searched for it one click away.
 *
 * The number shapes come from the manifest, which takes them from
 * `modules/cis_numbers.py`: nothing here restates the pattern (EA §5.5).
 * Pure: the shell draws the options.
 */
import type { ConsignmentOrders } from "../../data/api-types";
import { paths } from "../../routing/routes";
import type {
  ChildRow,
  ManifestColumn,
  ManifestObject,
  ObjectId,
  Paginated,
} from "./contract";
import { idColumnFields } from "./list-columns";
import { rowTarget } from "./list-includes";
import { emptyListQuery, formatListQuery } from "./list-query";
import type { ManifestIndex } from "./manifest";
import { formatColumnValue } from "./values";

export interface FindRecordMatch {
  object: ManifestObject;
  /** The record's own number: a line reference is cut back to its parent. */
  number: string;
  href: string;
}

export interface FindSearchOption {
  object: ManifestObject;
  text: string;
  href: string;
}

/** The record a CIS number (or a line of one) names, if the text is one. */
export function recogniseCisNumber(
  text: string,
  objects: readonly ManifestObject[],
): FindRecordMatch | null {
  const candidate = text.trim().toUpperCase();
  if (candidate === "") return null;
  for (const object of objects) {
    const { identifier } = object;
    if (identifier.kind !== "cis_number" || !object.record) continue;
    let number: string | null = null;
    if (identifier.pattern && new RegExp(identifier.pattern).test(candidate))
      number = candidate;
    else if (
      identifier.line_pattern &&
      new RegExp(identifier.line_pattern).test(candidate)
    )
      number = candidate.slice(0, candidate.lastIndexOf("."));
    if (number) {
      return {
        object,
        number,
        href: paths.objectRecord({ objectId: object.id, recordKey: number }),
      };
    }
  }
  return null;
}

/**
 * Text that might be a courier consignment number: one word of six characters or more with a digit in it. Only
 * a request saver (the shape of a carrier's number is the backend's to say, from delivery tracking); text that
 * passes is looked up, and the lookup answers with no orders for anything that is not one.
 */
export function consignmentCandidate(text: string): string | null {
  const candidate = text.trim();
  return /^\S{6,}$/.test(candidate) && /\d/.test(candidate) ? candidate : null;
}

export interface FindConsignmentMatch {
  orderNo: string;
  /** "Open Order ORD-K48193". */
  label: string;
  /** Why it matches: "Consignment 6012345678 · Acme Ltd". */
  hint: string;
  href: string;
}

/** The orders a consignment number belongs to, each as an option that opens its record. */
export function consignmentMatches(
  reference: string,
  orders: ConsignmentOrders["orders"],
): FindConsignmentMatch[] {
  return orders.map((order) => ({
    orderNo: order.order_no,
    label: `Open Order ${order.order_no}`,
    hint: `Consignment ${reference} · ${order.customer_name ?? order.title}`,
    href: paths.objectRecord({
      objectId: "orders.order",
      recordKey: order.order_no,
    }),
  }));
}

/**
 * The objects a bare Tharstern number may name, in the order Find offers them (Tharstern history spec 18 §3.2): a
 * Tharstern sales order, job or estimate number. `find` for Claude looks the same two up.
 */
export const THARSTERN_NUMBER_OBJECTS: readonly ObjectId[] = [
  "orders.tharstern_order",
  "quotes.tharstern_estimate",
];

/** Three to six digits: the shape of a Tharstern number. No CIS number is bare digits. */
export function tharsternNumberCandidate(text: string): string | null {
  const candidate = text.trim();
  return /^\d{3,6}$/.test(candidate) ? candidate : null;
}

export interface FindTharsternMatch {
  object: ManifestObject;
  /** The record's own number: a job number on a sales order answers that sales order's. */
  number: string;
  /** What the record is, shown beside the option: an order's description. */
  title: string;
  href: string;
}

/** The option that opens a Tharstern record the lookup found, or null when the record carries no number. */
export function tharsternMatch(
  object: ManifestObject,
  record: Record<string, unknown>,
): FindTharsternMatch | null {
  const number = record[object.identifier.field];
  if (typeof number !== "string" || number === "") return null;
  const title = object.record ? record[object.record.title_field] : null;
  return {
    object,
    number,
    title: typeof title === "string" ? title : "",
    href: paths.objectRecord({ objectId: object.id, recordKey: number }),
  };
}

/**
 * Whether Find's text search covers the object's list. A child with no record page of its own (an address) is not
 * searched: its list is for Claude to page, and staff reach it from its parent's record. A child that has a record
 * page (a pro-forma) is still searched, and so is a child an area places (`placedInAreas`: Customer users and
 * Supplier users sit in Parties).
 */
export function isSearchedByFind(
  object: ManifestObject,
  placedInAreas: readonly ObjectId[] = [],
): boolean {
  if (!object.list?.searchable) return false;
  return (
    object.parents.length === 0 ||
    object.record !== null ||
    placedInAreas.includes(object.id)
  );
}

/**
 * The objects people look up most, searched and shown first (FB-053): the sidebar's area order would put Needs
 * attention's alerts first, which nobody types a name to find.
 */
export const FIND_FIRST_OBJECTS: readonly ObjectId[] = [
  "customers.customer",
  "orders.order",
  "quotes.quote",
  "suppliers.supplier",
  "purchase_orders.purchase_order",
];

/** Find suggests records once this many characters are typed: fewer match too much to be worth the reads. */
export const FIND_SUGGEST_FROM_LENGTH = 3;

/**
 * The objects Find searches for text, in the order it shows them: `FIND_FIRST_OBJECTS`, then the rest in the order
 * given (the areas'). The caller passes only the objects that are on for the person: a switched-off
 * object has no records to offer.
 */
export function findSearchedObjects(
  objects: readonly ManifestObject[],
  placedInAreas: readonly ObjectId[] = [],
): ManifestObject[] {
  const searched = objects.filter((object) =>
    isSearchedByFind(object, placedInAreas),
  );
  const rank = (object: ManifestObject) => {
    const at = FIND_FIRST_OBJECTS.indexOf(object.id);
    return at === -1 ? FIND_FIRST_OBJECTS.length : at;
  };
  // Array sort is stable: the rest keep the order given.
  return searched.sort((a, b) => rank(a) - rank(b));
}

/**
 * Find's objects. `objects`: every declared object, the areas' first, for reading a number (a switched-off object
 * stays in, so its number still opens its "switched off" page). `searched`: those its text search reads, on for the
 * person, in `findSearchedObjects`' order. Read each render: whether an object is on can change with `/me`.
 */
export function findObjects(index: ManifestIndex): {
  objects: ManifestObject[];
  searched: ManifestObject[];
} {
  const inAreas = index.manifest.areas.flatMap((area) =>
    area.objects.map((o) => o.id),
  );
  const ordered = inAreas
    .filter((id) => index.isObjectKnown(id))
    .map((id) => index.objectOf(id));
  const rest = index.manifest.objects.filter((o) => !inAreas.includes(o.id));
  const objects = [...ordered, ...rest];
  return {
    objects,
    searched: findSearchedObjects(
      objects.filter((o) => index.isObjectEnabled(o.id)),
      inAreas,
    ),
  };
}

/** The object's list searched for the text: what a search option and a group's "Show all" open. */
export function findListHref(object: ManifestObject, text: string): string {
  return paths.objectList({
    objectId: object.id,
    search: formatListQuery({ ...emptyListQuery(), q: text.trim() }),
  });
}

/** One option per object searched, each opening its list searched for the text. */
export function findSearchOptions(
  text: string,
  searched: readonly ManifestObject[],
): FindSearchOption[] {
  const trimmed = text.trim();
  if (trimmed === "") return [];
  return searched.map((object) => ({
    object,
    text: trimmed,
    href: findListHref(object, trimmed),
  }));
}

/** One record a search of an object's list found. */
export interface FindRecordHit {
  /** The object the record is: the list's own, or the second object a mixed list holds (a Tharstern order). */
  object: ObjectId;
  key: string;
  title: string;
  /**
   * Its number, and why it matched when the title does not show it: "CUS-E03962 · Tharstern code RAM1379". Null
   * when there is neither (a ticket's number is an internal id).
   */
  detail: string | null;
  /** Its record page, or for a list-only object its list searched for the text. */
  href: string;
}

/** What one object's list returned for the text: the first few records, and how many match in all. */
export interface FindRecordGroup {
  object: ManifestObject;
  total: number;
  hits: FindRecordHit[];
  /** The list searched for the text, holding all `total` of them. */
  listHref: string;
}

function shownText(value: unknown, column: ManifestColumn | undefined): string {
  if (value == null || value === "") return "";
  return column ? formatColumnValue(value, column) : String(value);
}

/**
 * A page of an object's list searched for the text, as hits that open their records. A hit shows its number (unless
 * the number is an internal id) and the column the text was found in when its title does not hold it (a customer
 * found by its Tharstern code), so the person sees why it matched.
 */
export function findRecordGroup(
  index: ManifestIndex,
  object: ManifestObject,
  text: string,
  page: Paginated<ChildRow>,
): FindRecordGroup {
  const list = object.list;
  if (!list) throw new Error(`${object.id} has no list for Find to search.`);
  const needle = text.trim().toLowerCase();
  const listHref = findListHref(object, text);
  const hidden = idColumnFields(list, object.identifier.kind, page.items);
  const titleColumn = list.columns.find((c) => c.field === list.title_field);
  const hits = page.items.map((row): FindRecordHit => {
    const target = rowTarget(index, object.id, list, row);
    const title =
      shownText(row[list.title_field], titleColumn) || target.key || "—";
    const matched = list.columns.find(
      (column) =>
        column.field !== list.title_field &&
        column.field !== list.key_field &&
        !hidden.has(column.field) &&
        shownText(row[column.field], column).toLowerCase().includes(needle),
    );
    // The number is worth showing only where the list itself shows it: not a uuid, nor an id it has no column for.
    const keyField = target.isIncluded
      ? index.objectOf(target.object).identifier.field
      : list.key_field;
    const isKeyShown =
      target.key !== "" &&
      target.key !== title &&
      list.columns.some((c) => c.field === keyField) &&
      !hidden.has(keyField);
    const detail =
      [
        isKeyShown ? target.key : null,
        matched
          ? `${matched.label} ${shownText(row[matched.field], matched)}`
          : null,
      ]
        .filter((part) => part !== null)
        .join(" · ") || null;
    const opensRecord =
      target.key !== "" &&
      index.isObjectKnown(target.object) &&
      index.objectOf(target.object).record !== null;
    return {
      object: target.object,
      key: target.key,
      title,
      detail,
      href: opensRecord
        ? paths.objectRecord({ objectId: target.object, recordKey: target.key })
        : listHref,
    };
  });
  return { object, total: page.total, hits, listHref };
}
